import { FC, useState } from 'react';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  Spacing,
  StyledDiv,
  Typography,
  TypographyVariant,
} from '@taleemabad/dsm/components';
import {
  PrefixSuffixContent,
  PrefixSuffixContentProps,
} from '../../shared/shared';
import { Position } from '@taleemabad/shared';

interface ReportCardQuestionsListProps {
  heading: string;
  headingVariant?: TypographyVariant;
  supportingText?: string;
  supportingTextVariant?: TypographyVariant;
  bottomText?: string;
  bottomTextVariant?: TypographyVariant;
  trailingSupportingText?: string;
  trailingSupportingTextVariant?: TypographyVariant;
  trailingSupportingTextPosition?: Position;
  trailingIcon?: PrefixSuffixContentProps;
  onTrailingIconClick?: () => void;
}

const TEXT_LENGTH_LIMIT = 200;

export const ReportCardQuestionsList: FC<ReportCardQuestionsListProps> = ({
  heading,
  headingVariant = 'textBodyLargeOnSurface',
  supportingText,
  supportingTextVariant = 'textBodyMediumOnSurfaceVariant',
  bottomText,
  bottomTextVariant = 'textBodyMediumOnSurfaceVariant',
  trailingSupportingText,
  trailingSupportingTextVariant = 'textBodyMediumOnSurfaceVariant',
  trailingSupportingTextPosition = Position.Top,
  trailingIcon,
  onTrailingIconClick,
}) => {
  const [isTruncated, setIsTruncated] = useState(true);

  const toggleEllipsis = () => {
    setIsTruncated((prev) => !prev);
  };

  return (
    <StyledListCardWrapper>
      <StyledListCard>
        {heading && (
          <StyledHeadlineWrapper>
            <Typography variant={headingVariant}>{heading}</Typography>
          </StyledHeadlineWrapper>
        )}

        {trailingSupportingText && (
          <StyledTrailingContainer
            trailingSupportingTextPosition={trailingSupportingTextPosition}
          >
            <Typography ellipseLine={2} variant={trailingSupportingTextVariant}>
              {trailingSupportingText}
            </Typography>
            {trailingIcon && (
              <StyledDiv justifyContent="flex-end">
                <PrefixSuffixContent
                  {...trailingIcon}
                  onClick={onTrailingIconClick}
                />
              </StyledDiv>
            )}
          </StyledTrailingContainer>
        )}
      </StyledListCard>

      {supportingText && (
        <Spacing paddingTop="xl" paddingBottom="xl">
          <Typography variant={supportingTextVariant}>
            {supportingText.length > TEXT_LENGTH_LIMIT ? (
              <>
                {isTruncated ? (
                  <>{supportingText.substring(0, TEXT_LENGTH_LIMIT)}...</>
                ) : (
                  <>{supportingText}</>
                )}
                <StyledElipseTextWrapper>
                  <Typography
                    variant="textBodyMediumBlue400"
                    onClick={toggleEllipsis}
                    cursor="pointer"
                  >
                    {isTruncated ? 'See more' : 'See less'}
                  </Typography>
                </StyledElipseTextWrapper>
              </>
            ) : (
              <>{supportingText}</>
            )}
          </Typography>
        </Spacing>
      )}

      {bottomText && (
        <Spacing>
          <Typography variant={bottomTextVariant}>{bottomText}</Typography>
        </Spacing>
      )}
    </StyledListCardWrapper>
  );
};

const StyledListCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: transparent;
  padding: var(--rk-spacing-xxl) var(--rk-spacing-lg);
`;

const StyledListCard = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  gap: var(--rk-spacing-xxxl);
  width: 100%;
`;

interface StyledListProps {
  trailingSupportingTextPosition?: Position;
}

const StyledTrailingContainer = styled.div<StyledListProps>`
  display: flex;
  align-items: flex-start;
  text-align: right;
  min-width: 100px;
  gap: var(--rk-spacing-xl);
  align-self: ${({ trailingSupportingTextPosition }) =>
    trailingSupportingTextPosition === Position.Top
      ? 'flex-start'
      : trailingSupportingTextPosition === Position.Bottom
        ? 'flex-end'
        : 'center'};

  ${mq(BreakpointSizes.xs)} {
    min-width: unset;
    flex-direction: column;
    gap: var(--rk-spacing-sm);
    align-items: flex-end;
  }
`;

const StyledElipseTextWrapper = styled.div`
  color: var(--md-sys-utility-gray-blue-500);
  margin-bottom: var(--rk-spacing-xs);
`;

export const StyledHeadlineWrapper = styled.div`
  overflow-wrap: anywhere;
`;
